import { useState, useRef, useCallback, useEffect } from 'react';
import type { RefObject } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de error de cámara
// ─────────────────────────────────────────────────────────────────────────────

const CAMERA_ERROR_TYPE = {
  NOT_SUPPORTED: 'not_supported',       // navigator.mediaDevices no disponible en el browser
  PERMISSION_DENIED: 'permission_denied', // usuario denegó el permiso de cámara
  NOT_FOUND: 'not_found',              // no hay dispositivo de cámara disponible
  UNKNOWN: 'unknown',                  // cualquier otro error inesperado
} as const;

type CameraErrorType = (typeof CAMERA_ERROR_TYPE)[keyof typeof CAMERA_ERROR_TYPE];

interface CameraError {
  type: CameraErrorType;
  message: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Opciones y retorno del hook
// ─────────────────────────────────────────────────────────────────────────────

interface UseCameraOptions {
  // 'user' = cámara frontal (para liveness), 'environment' = cámara trasera (para documentos)
  facingMode?: 'user' | 'environment';
  width?: number;
  height?: number;
}

interface UseCameraReturn {
  // Stream activo de la cámara o null si no hay conexión
  stream: MediaStream | null;
  // Ref que debe conectarse al elemento <video> en el JSX del componente
  videoRef: RefObject<HTMLVideoElement>;
  // true cuando el stream está activo y el video está listo para reproducirse
  isReady: boolean;
  // Error de cámara o null si no hubo ninguno
  error: CameraError | null;
  // Solicita acceso a la cámara y conecta el stream al elemento de video
  start: () => Promise<void>;
  // Detiene todos los tracks del stream y libera la cámara
  stop: () => void;
  // Captura el frame actual del video como data URL JPEG; retorna null si no está listo
  captureFrame: () => string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers internos
// ─────────────────────────────────────────────────────────────────────────────

// Mapea el nombre del DOMException de getUserMedia a nuestro enum de errores
function mapDOMExceptionToCameraError(err: unknown): CameraError {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return {
          type: CAMERA_ERROR_TYPE.PERMISSION_DENIED,
          message: 'Permiso de cámara denegado. Habilitá el acceso desde la configuración del navegador.',
        };
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return {
          type: CAMERA_ERROR_TYPE.NOT_FOUND,
          message: 'No se encontró ninguna cámara disponible en este dispositivo.',
        };
      default:
        return {
          type: CAMERA_ERROR_TYPE.UNKNOWN,
          message: `Error de cámara inesperado: ${err.message}`,
        };
    }
  }

  const message = err instanceof Error ? err.message : 'Error desconocido al acceder a la cámara.';
  return { type: CAMERA_ERROR_TYPE.UNKNOWN, message };
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook principal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Provee acceso a la cámara del dispositivo con manejo de errores, captura de
 * frames y limpieza automática al desmontar el componente.
 *
 * Uso para documentos: facingMode = 'environment' (cámara trasera)
 * Uso para liveness:   facingMode = 'user' (cámara frontal)
 */
export function useCamera(options: UseCameraOptions = {}): UseCameraReturn {
  const {
    facingMode = 'environment', // trasera por defecto para captura de documentos
    width,
    height,
  } = options;

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<CameraError | null>(null);

  // Ref al elemento <video> — el componente que use este hook debe asignarlo a videoRef
  const videoRef = useRef<HTMLVideoElement>(null);

  // Detiene todos los tracks del stream activo y libera el recurso de hardware
  const stop = useCallback((): void => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStream(null);
    setIsReady(false);
  }, [stream]);

  // Solicita acceso a la cámara con las opciones configuradas
  const start = useCallback(async (): Promise<void> => {
    // Verifica que la API está disponible en este browser/contexto (requiere HTTPS)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError({
        type: CAMERA_ERROR_TYPE.NOT_SUPPORTED,
        message: 'Este navegador no soporta acceso a la cámara. Usá un navegador moderno con HTTPS.',
      });
      return;
    }

    // Resetea el estado de error antes de intentar
    setError(null);

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode,
          ...(width !== undefined && { width: { ideal: width } }),
          ...(height !== undefined && { height: { ideal: height } }),
        },
        audio: false, // KYC solo necesita video
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);

      // Conecta el stream al elemento <video> cuando ya esté disponible en el DOM
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;

        // isReady solo se activa cuando el video tiene dimensiones reales disponibles
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch(() => {
            // Ignorar errores de autoplay; el usuario puede iniciar manualmente
          });
          setIsReady(true);
        };
      }
    } catch (err) {
      setError(mapDOMExceptionToCameraError(err));
    }
  }, [facingMode, width, height]);

  // Captura el frame actual del video y lo devuelve como data URL JPEG
  const captureFrame = useCallback((): string | null => {
    if (!isReady || !videoRef.current) {
      return null;
    }

    const video = videoRef.current;

    // Crea un canvas temporal del mismo tamaño que el video
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Calidad 0.9 balancea tamaño de archivo vs fidelidad para OCR/scoring
    return canvas.toDataURL('image/jpeg', 0.9);
  }, [isReady]);

  // Limpieza automática al desmontar — libera la cámara para que el browser no
  // mantenga el indicador de cámara activa después de que el componente se desmonte
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
    // Solo se ejecuta en desmontaje; no necesita reaccionar a cambios de stream
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    stream,
    videoRef,
    isReady,
    error,
    start,
    stop,
    captureFrame,
  };
}

// Re-exportar tipos y constantes para uso en componentes
export { CAMERA_ERROR_TYPE };
export type { CameraErrorType, CameraError, UseCameraOptions, UseCameraReturn };
